import React, { useState, useRef } from "react";
// import excel
import * as XLSX from "xlsx";

//import api calls
import { saveExcelData, fetchFailedInvoices, fetchDuplicateInvoices } from "./Transactions";

//page management
import { CallStates } from "../../../components/SharedComponents/AppStates/Utils";

const InvoiceUploadStep = ({
  api,
  setFailedRows,
  moveToNextStep,
  setCurrentStep,
}) => {
  const [excelData, setExcelData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [callState, setCallState] = useState(CallStates.INITIAL);
  const [viewLoading, setViewLoading] = useState(null); 
  const fileInputRef = useRef(null);
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rowCount = XLSX.utils.decode_range(worksheet["!ref"]).e.r + 1;

        const invoiceList = [];
        for (let row = 2; row <= rowCount; row++) {
          const invoiceId = worksheet[`A${row}`]?.v;
          if (invoiceId === undefined || invoiceId === null || invoiceId === "") {
            continue;
          }
          invoiceList.push({
            InvoiceId: invoiceId,
            Amount: parseFloat(worksheet[`B${row}`]?.v) || 0,
          });
        }
        setExcelData(invoiceList);
      };
      reader.readAsArrayBuffer(file);
    }
  };
  const handleClearFile = () => {
    setExcelData([]);
    fileInputRef.current.value = "";
    setErrorMessage(null);
    setSuccessMessage(null);
  };
  const viewFailedInvoices = async () => {
    if (viewLoading) {
      return;
    }
    setErrorMessage(null);
    setViewLoading("failed");
    try {
      const response = await fetchFailedInvoices(api);
      if (response.InvoiceListItems.length > 0) {
        setFailedRows(response.InvoiceListItems);
        setCurrentStep("review");
      } else {
        setErrorMessage("No failed invoices available");
      }
    } catch (error) {
      setErrorMessage("Failed to load invoice details");
    } finally {
      setViewLoading(null);
    }
  };
  const viewDuplicateInvoices = async () => {
    if (viewLoading) {
      return;
    }
    setErrorMessage(null);
    setViewLoading("duplicates");
    try {
      const response = await fetchDuplicateInvoices(api);
      if (response.InvoiceListItems.length > 0) {
        setFailedRows(response.InvoiceListItems);
        setCurrentStep("duplicates");
      } else {
        setErrorMessage("No duplicate invoices available");
      }
    } catch (error) {
      setErrorMessage("Failed to load invoice details");
    } finally {
      setViewLoading(null);
    }
  };
  // Upload Excel data to API
  const handleSubmitAll = async (event) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setCallState(CallStates.SAVING);
    const summary = { invoicelist: excelData, module: "SaveData" };
    try {
      await saveExcelData(api, summary, setCallState);
      setExcelData([]);
      fileInputRef.current.value = "";
      setSuccessMessage("Upload successful. Results will be available after the next scheduled job run (12 PM or 10 PM) — check \"View Failed Invoices\" above to see them.")
      // fetching failed invoices
    //   const response = await fetchFailedInvoices(api);
    //   if (response.InvoiceListItems.length > 0) {
    //     setFailedRows(response.InvoiceListItems);
    //     moveToNextStep();
    //   }
    } catch (error) {
      setErrorMessage("Failed to upload invoices");
    } finally {
        setCallState(CallStates.INITIAL); // Ensure loader is hidden
    }
  };

  return (
    <div className="invoice-upload-container">
      <div className="invoice-view-links">
        <div className="invoice-view-link-group">
          <a
            href="#"
            className={
              "invoice-table-link" +
              (viewLoading ? " invoice-table-link-disabled" : "")
            }
            aria-disabled={Boolean(viewLoading)}
            onClick={(e) => {
              e.preventDefault();
              viewFailedInvoices();
            }}
          >
            {viewLoading === "failed" && (
              <span className="react-ExcelUpload-spinner" />
            )}
            View Failed Invoices
          </a>
          <span className="invoice-view-link-hint">
            Results appear after the next scheduled job run (12&nbsp;PM or 10&nbsp;PM)
          </span>
        </div>
        <div className="invoice-view-link-group">
          <a
            href="#"
            className={
              "invoice-table-link" +
              (viewLoading ? " invoice-table-link-disabled" : "")
            }
            aria-disabled={Boolean(viewLoading)}
            onClick={(e) => {
              e.preventDefault();
              viewDuplicateInvoices();
            }}
          >
            {viewLoading === "duplicates" && (
              <span className="react-ExcelUpload-spinner" />
            )}
            View Duplicate Invoices
          </a>
          <span className="invoice-view-link-hint">
            Shows duplicates present in the uploaded sheet
          </span>
        </div>
      </div>
      <h2>Please Upload Invoice Details Spreadsheet</h2>
      <div className="invoice-upload-instructions">
        <div className="instructions-header">
          ⚠ Important Upload Instructions
        </div>

        <ul className="instructions-list">
          <li>
            <strong>Do NOT re-upload the Excel file</strong> if you receive an error.
            Please contact the IT Team immediately.
          </li>

          <li>
            Take a screenshot of the error and raise a <strong>Halo ticket</strong> including:
            <ul className="sub-list">
              <li>Screenshot of the error</li>
              <li>Clear description of the issue</li>
              <li>Exact time the error occurred</li>
            </ul>
          </li>

          <li>
           After receiving the <strong>success message</strong>, wait for the job run.
           The job is scheduled two times daily — at 12 PM and 10 PM.
           After the job run, you will be able to verify the cases and confirm that payment transactions have been processed correctly.
          </li>
        </ul>
      </div>
      <input
        className="invoice-bulk-file-name"
        type="file"
        accept=".xlsx, .xls"
        onChange={handleFileUpload}
        ref={fileInputRef}
      />
      
      {excelData.length > 0 && (
        <div className="react-ExcelUpload">
        <div className="invoice-upload-actions">
          <button onClick={handleSubmitAll} disabled={callState === CallStates.SAVING}>
            {callState === CallStates.SAVING && (
              <span className="react-ExcelUpload-spinner" />
            )}
            Submit All
          </button>
          <button
            type="button"
            className="invoice-clear-button"
            onClick={handleClearFile}
            disabled={callState === CallStates.SAVING}
          >
            ✕ Clear
          </button>
        </div>
         {callState === CallStates.SAVING && (
                        <div className="invoice-upload-loading">Uploading invoices, please wait...</div>
                    )}
        </div>
      )}
      {errorMessage && <div className="invoice-bulk-error-message">{errorMessage}</div>}
      {successMessage && <div className="invoice-bulk-message-success">{successMessage}</div>}
   
    </div>
  );
};

export default InvoiceUploadStep;
